import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Shield,
  FileText,
  Cookie,
  Database,
  Users,
  Globe,
  Clock,
  UserCheck,
  Baby,
  Cpu,
  RefreshCw,
  Mail,
  Scale,
  AlertCircle,
} from "lucide-react";

import { Button } from "../components/ui/button";

/**
 * PrivacyPolicyPage — GDPR-structured disclosure for saturnstyling.ie.
 *
 * Every section maps to a specific GDPR obligation:
 *   §1  Controller identity          → Art 13(1)(a)
 *   §2  Categories of data collected  → Art 13(1)(c) / 14(1)(d)
 *   §3  Legal basis per category      → Art 6 / Art 13(1)(c)
 *   §4  Cookies + Consent Mode        → ePrivacy Regulation + PECR
 *   §5  Third-party recipients        → Art 13(1)(e)
 *   §6  Retention periods             → Art 13(2)(a) — MATCHED to
 *                                        services/retention.py constants
 *   §7  Data subject rights           → Art 13(2)(b) / Chapter III
 *   §8  Supervisory authority right   → Art 13(2)(d) — DPC (Ireland)
 *   §9  Children's data               → Art 8
 *   §10 Automated decision-making     → Art 22 — see FLAG in text
 *   §11 Policy-update process         → Art 13(3)
 *   §12 Contact for privacy queries   → Art 13(1)(b)
 *
 * The retention numbers in §6 are read from actual code constants
 * (see backend/services/retention.py). If those constants change, this
 * page MUST be updated to match — the wording deliberately mirrors the
 * variable names so a grep across both files catches drift.
 *
 * This is a strong-draft — solicitor review is recommended before
 * treating it as final legal text.
 */
const PrivacyPolicyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-saturn-black text-white">
      <header className="border-b border-slate-800 bg-saturn-black/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_tint-bay-engine/artifacts/zdb6hh5n_Eima%20logo%20Speed%20rate%20font%2020x20.png"
              alt="Saturn Styling"
              className="w-10 h-10 object-contain"
            />
            <span className="font-orbitron font-bold text-lg text-white">
              SATURNSTYLING
            </span>
          </Link>
          <Link to="/">
            <Button
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12" data-testid="privacy-policy-page">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="font-orbitron text-3xl md:text-4xl font-bold text-white mb-2">
            Privacy <span className="text-saturn-orange">Policy</span>
          </h1>
          <p className="text-slate-400 mb-8">Last updated: February 2026</p>

          {/* Summary card */}
          <div className="glass rounded-xl p-6 mb-8 border-saturn-orange/30">
            <h2 className="font-rajdhani font-bold text-lg text-saturn-orange mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5" /> Quick Summary
            </h2>
            <p className="text-slate-300 leading-relaxed">
              We collect the information we genuinely need to book, deliver
              and back up your service — nothing more. We never sell your
              data. You can ask us at any time what we hold, correct it,
              have it erased, or withdraw your consent to analytics and
              advertising cookies. Full detail on every point is below.
            </p>
          </div>

          {/* §1 Controller */}
          <Section icon={Shield} title="1. Who we are (Data Controller)">
            <p>
              <strong className="text-white">Saturn Styling</strong> is the
              data controller for the personal information collected via
              this website and during service delivery.
            </p>
            <ul className="mt-2 space-y-1 text-sm">
              <li><strong>Trading address:</strong> Duleek, Co. Meath, Ireland</li>
              <li><strong>Contact for privacy matters:</strong>{" "}
                <a href="mailto:saturnstyling@gmail.com" className="text-saturn-orange hover:underline">
                  saturnstyling@gmail.com
                </a>
              </li>
            </ul>
            <p className="mt-2 text-sm text-slate-400">
              As a small business we are not required to designate a formal
              Data Protection Officer, but the address above reaches the
              person responsible for privacy matters and is monitored.
            </p>
          </Section>

          {/* §2 Categories of data */}
          <Section icon={Database} title="2. What personal data we collect">
            <p>Depending on how you interact with us, we may collect:</p>
            <ul className="mt-3 space-y-2">
              <Li><strong>Contact details</strong> — your name, email address, and phone number, provided when you book, request a quote, or contact us.</Li>
              <Li><strong>Vehicle details</strong> — make, model, year, size class, and registration number. The registration is used to track warranty coverage for the tint / film applied to that specific vehicle.</Li>
              <Li><strong>Booking &amp; service history</strong> — the services you booked, dates, notes you provided, and photos taken before / after the job for our own quality-assurance record.</Li>
              <Li><strong>Payment-related data</strong> — we do <strong>not</strong> store your card number, CVC, or bank details. Payments are processed directly by our payment provider (Revolut Business); we only receive a transaction reference, the amount, and whether the payment succeeded or failed.</Li>
              <Li><strong>Technical / analytics data</strong> — cookies and similar technologies may collect approximate location (inferred from IP address), device and browser type, referring website, pages viewed, and interactions on the site. This category is <strong>only collected once you accept analytics/advertising cookies</strong> via the cookie banner (see §4).</Li>
              <Li><strong>Communications</strong> — any email or message you send us and our replies, retained so we can pick up the thread if you contact us again.</Li>
            </ul>
          </Section>

          {/* §3 Legal basis */}
          <Section icon={Scale} title="3. Legal basis for processing (GDPR Article 6)">
            <p>Each category above is processed under a specific legal basis. We do not use one blanket justification.</p>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm border border-slate-800 rounded-lg overflow-hidden">
                <thead className="bg-slate-900/50">
                  <tr>
                    <th className="text-left px-3 py-2 font-rajdhani text-slate-300">Data</th>
                    <th className="text-left px-3 py-2 font-rajdhani text-slate-300">Legal basis</th>
                  </tr>
                </thead>
                <tbody className="text-slate-300">
                  <Row d="Contact + vehicle + booking details" b='Contract necessity (Art 6(1)(b)) — we cannot deliver the service you booked without them.' />
                  <Row d="Payment reference from Revolut" b='Contract necessity (Art 6(1)(b)) + legal obligation (Art 6(1)(c)) for Revenue.ie financial records.' />
                  <Row d="Before/after job photos (for quality assurance, not for public marketing)" b='Legitimate interest (Art 6(1)(f)) — evidence of workmanship in case of a warranty claim. Weighed against your reasonable expectations of privacy.' />
                  <Row d="Analytics + advertising cookies (Google Analytics 4, Google Ads, Meta Pixel)" b='Consent (Art 6(1)(a)), captured via the cookie banner. Withdrawable at any time.' />
                  <Row d="Marketing emails (if you opt in)" b='Consent (Art 6(1)(a)). One-click unsubscribe on every email.' />
                  <Row d="Transactional emails (booking confirmations, warranty reminders, invoices)" b='Contract necessity (Art 6(1)(b)) — not marketing, so no separate consent required.' />
                  <Row d="Basic security logs (IP, timestamp on failed logins / suspicious activity)" b='Legitimate interest (Art 6(1)(f)) — fraud and account-abuse prevention.' />
                </tbody>
              </table>
            </div>
          </Section>

          {/* §4 Cookies */}
          <Section icon={Cookie} title="4. Cookies &amp; Consent Mode">
            <p>
              Cookies fall into two groups on this site. Essential cookies
              are strictly necessary for the site to function — session
              handling, cart/booking state, security. These are always on
              and cannot be disabled without breaking the site.
            </p>
            <p className="mt-2">
              Non-essential cookies — analytics and advertising — are
              controlled by the cookie banner shown on your first visit:
            </p>
            <ul className="mt-3 space-y-2">
              <Li>
                <strong>Google Analytics 4</strong> (measurement ID{" "}
                <code className="text-xs bg-slate-800 px-1.5 py-0.5 rounded">G-NE49KEYXJ5</code>
                ) — measures how visitors reach and use the site, entirely
                aggregated, so we know which pages are useful and which
                need work.
              </Li>
              <Li>
                <strong>Google Ads</strong> (conversion ID{" "}
                <code className="text-xs bg-slate-800 px-1.5 py-0.5 rounded">AW-10792513724</code>
                ) — tells us which of our own paid Google ads actually led
                to a paying customer, so ad budget goes where it works.
              </Li>
              <Li>
                <strong>Meta Pixel</strong> (pixel ID{" "}
                <code className="text-xs bg-slate-800 px-1.5 py-0.5 rounded">1031124436624972</code>
                ) — tells us which of our own paid Facebook and Instagram
                ads actually led to a paying customer, so ad budget goes
                where it works. Same purpose as Google Ads above, just on
                Meta's advertising network. Sets cookies like{" "}
                <code className="text-xs bg-slate-800 px-1.5 py-0.5 rounded">_fbp</code>{" "}
                only after you accept — never before.
              </Li>
            </ul>
            <p className="mt-3">
              All three (Google Analytics 4, Google Ads, and Meta Pixel)
              are governed by <strong>consent-first defaults</strong>: we
              set every tracking pixel to <em>denied</em> / <em>revoked</em>
              on every first visit, so no analytics or advertising cookies
              are set until you explicitly click{" "}
              <strong>"Accept All"</strong> on the banner. If you click{" "}
              <strong>"Reject Non-Essential"</strong>, Google still
              receives fully anonymous, modelled signal (per its Consent
              Mode v2 behaviour) and Meta receives nothing at all, but no
              cookies or identifiers are stored on your device. Your
              choice is remembered so the banner does not reappear on
              every visit; you can change your mind by clearing site data
              for saturnstyling.ie in your browser, which will re-show
              the banner.
            </p>
          </Section>

          {/* §5 Third parties */}
          <Section icon={Users} title="5. Who we share your data with">
            <p>We share the minimum necessary personal data with the following processors, each under a data-processing agreement:</p>
            <ul className="mt-3 space-y-2">
              <Li>
                <strong>Revolut Business</strong> (Ireland / EEA) — processes card payments. They receive your card details directly; we never see them.
              </Li>
              <Li>
                <strong>Resend</strong> (email delivery infrastructure) — delivers our transactional emails (booking confirmations, invoices, warranty reminders). They receive your name, email, and the content of the email.
              </Li>
              <Li>
                <strong>Google Ireland Limited</strong> (Google Analytics 4 &amp; Google Ads) — receives the analytics / advertising signal described in §4, once you have accepted the corresponding cookies.
              </Li>
              <Li>
                <strong>Meta Platforms Ireland Limited</strong> (Meta Pixel — Facebook &amp; Instagram advertising) — receives the advertising / conversion signal described in §4, once you have accepted the corresponding cookies. Data is used solely to attribute our own paid Facebook / Instagram ads to real bookings, not for cross-site profiling on our end.
              </Li>
              <Li>
                <strong>Emergent</strong> (hosting platform) — the underlying infrastructure that runs the site. All data is stored on their systems.
              </Li>
            </ul>
            <div className="mt-4 p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-start gap-3">
              <Globe className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-rajdhani font-bold text-amber-300">
                  International transfers outside the EEA
                </p>
                <p className="text-sm text-slate-300 mt-1">
                  Google Analytics, Google Ads, and Meta Pixel involve
                  transferring limited personal data (including online
                  identifiers such as cookie IDs and IP address, as
                  scoped by Consent Mode v2 for Google and by Meta's
                  own consent-revoke behaviour for Meta) to servers in
                  the United States. Google Ireland Limited and Meta
                  Platforms Ireland Limited are the data exporters for
                  EEA visitors, and the transfers rely on the{" "}
                  <strong>EU-US Data Privacy Framework</strong>{" "}
                  (both Google LLC and Meta Platforms, Inc. are
                  certified participants) and, as a backup, the
                  European Commission's{" "}
                  <strong>Standard Contractual Clauses (SCCs)</strong>. This
                  is disclosed here per GDPR Article 13(1)(f).
                </p>
              </div>
            </div>
            <p className="mt-3 text-sm text-slate-400">
              We do not sell personal data. We do not share personal data
              with advertisers for their own independent marketing.
            </p>
          </Section>

          {/* §6 Retention */}
          <Section icon={Clock} title="6. How long we keep your data">
            <p>Retention periods are enforced automatically by a nightly job — they are not just written down here:</p>
            <ul className="mt-3 space-y-2">
              <Li>
                <strong>Cancelled, no-show, or unpaid bookings:</strong> deleted after <strong>12 months</strong>. No invoice was issued, so there is no ongoing legal reason to keep the record.
              </Li>
              <Li>
                <strong>Paid bookings and their invoices:</strong> retained for <strong>7 years</strong> — the retention period required by Irish Revenue for financial records — then <strong>anonymised</strong>. Your name, email, phone, and free-text notes are replaced with tokens; the financial row itself (amount, VAT, date) survives so tax/accounting continuity is preserved.
              </Li>
              <Li>
                <strong>Unconverted quote requests:</strong> deleted after <strong>18 months</strong> if they do not lead to a booking.
              </Li>
              <Li>
                <strong>Marketing consent + email address (if you opted in):</strong> kept while your consent is active. Withdrawing consent removes you from the list within one working day.
              </Li>
              <Li>
                <strong>Website analytics events (Google Analytics 4):</strong> retained per Google's own configuration (default 2 months for user-level data, 14 months for aggregate reports; adjustable in the GA4 property settings).
              </Li>
            </ul>
          </Section>

          {/* §7 Rights */}
          <Section icon={UserCheck} title="7. Your rights under GDPR">
            <p>You have all of the following rights over your personal data. To exercise any of them, email{" "}
              <a href="mailto:saturnstyling@gmail.com" className="text-saturn-orange hover:underline">
                saturnstyling@gmail.com
              </a>{" "}
              stating clearly which right(s) you want to exercise:
            </p>
            <ul className="mt-3 space-y-2">
              <Li><strong>Right of access</strong> (Art 15) — a copy of the personal data we hold about you, and a description of how it is used.</Li>
              <Li><strong>Right to rectification</strong> (Art 16) — correction of anything inaccurate or out of date.</Li>
              <Li><strong>Right to erasure</strong> (Art 17) — deletion, subject to the legal retention obligations described in §6 above (we cannot delete a paid invoice within its 7-year Revenue retention window, but we can anonymise it).</Li>
              <Li><strong>Right to restriction of processing</strong> (Art 18) — we stop using your data for anything beyond storage while a dispute or verification is resolved.</Li>
              <Li><strong>Right to data portability</strong> (Art 20) — receive the data you provided to us in a structured, commonly used, machine-readable format (JSON or CSV).</Li>
              <Li><strong>Right to object</strong> (Art 21) — object to processing based on legitimate interest (§3), including for direct marketing.</Li>
              <Li><strong>Right to withdraw consent</strong> (Art 7(3)) — at any time, for anything relying on consent (cookies, marketing emails). Withdrawal does not affect the lawfulness of any processing already carried out.</Li>
            </ul>
            <p className="mt-4 text-sm text-slate-300">
              <strong>Response timeframe:</strong> we will respond within{" "}
              <strong>one month</strong> of receiving your request, as
              required by GDPR Article 12(3). For complex or numerous
              requests we may extend this by up to <strong>two further
              months</strong>, in which case we will tell you within the
              first month why the extension is needed. There is no fee
              unless a request is manifestly unfounded or excessive.
            </p>
          </Section>

          {/* §8 DPC */}
          <Section icon={AlertCircle} title="8. Right to complain to the Data Protection Commission">
            <p>
              If you are not satisfied with how we have handled a request
              or with any aspect of our data processing, you have the right
              to lodge a complaint with the Irish supervisory authority:
            </p>
            <div className="mt-3 p-4 rounded-lg bg-slate-900/50 border border-slate-800">
              <p className="font-rajdhani font-bold text-white">
                Data Protection Commission (DPC)
              </p>
              <p className="text-sm text-slate-300 mt-1">
                21 Fitzwilliam Square South, Dublin 2, D02 RD28, Ireland
              </p>
              <p className="text-sm mt-2">
                <a
                  href="https://www.dataprotection.ie"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-saturn-orange hover:underline"
                  data-testid="dpc-link"
                >
                  dataprotection.ie
                </a>
              </p>
            </div>
            <p className="mt-3 text-sm text-slate-400">
              You can also lodge a complaint with the supervisory
              authority in the EU member state where you live or work, if
              different from Ireland.
            </p>
          </Section>

          {/* §9 Children */}
          <Section icon={Baby} title="9. Children's data">
            <p>
              Our service is not directed at children under 16. We do not
              knowingly collect personal data from anyone under 16, and we
              would not ordinarily enter a service contract with a minor
              for vehicle work — the vehicle owner would be an adult. If
              you believe a child has provided us with personal data in
              error, please contact us and we will delete it promptly.
            </p>
          </Section>

          {/* §10 Automated decision-making */}
          <Section icon={Cpu} title="10. Automated decision-making">
            <p>
              We do not use personal data to make automated decisions that
              produce legal effects or similarly significant effects on you
              within the meaning of GDPR Article 22.
            </p>
            <p className="mt-2">
              Where we run promotional draws (for example, our
              weighted-random or single-winner promotion campaigns), the
              outcome of the draw — for instance a percentage discount or
              a free add-on — is determined by an automated random draw
              rather than a human decision, but the resulting outcome is a{" "}
              <strong>benefit applied to a booking that you have chosen
              to enter</strong>. It does not deny a service, alter your
              legal status, or produce a similarly significant negative
              effect. On that basis we treat these draws as outside the
              scope of Article 22. If you would prefer not to participate
              in an active promotion, you can complete a booking without
              opting in.
            </p>
            <p className="mt-3">
              <strong className="text-white">How the draw actually works, in plain language:</strong>{" "}
              when you book during an active promotion, an entry is
              automatically created for you and a randomised process
              selects an outcome from the campaign's prize table. Outcomes
              range from small discounts — the most common result — up to
              larger prizes that appear rarely, because the odds are
              weighted so rarer prizes come up less often than everyday
              ones. The mechanism is entirely additive: the worst possible
              outcome is no additional discount, in which case you simply
              pay the price you already agreed at checkout. You are never
              charged more than the booking price you confirmed as a
              result of the draw. The specific odds and the full list of
              possible outcomes for a given campaign are kept
              server-side and not published, both to preserve the fairness
              of the draw and to prevent gaming the entry mechanism; the
              general terms for the campaign you entered are displayed on
              our{" "}
              <Link to="/promotion-terms" className="text-saturn-orange hover:underline">
                Current Promotion Terms
              </Link>{" "}
              page while that campaign is live.
            </p>
          </Section>

          {/* §11 Updates */}
          <Section icon={RefreshCw} title="11. Changes to this policy">
            <p>
              We may update this policy from time to time — for example,
              if we add a new integration, change a retention period, or
              respond to a regulatory update. The date at the top of the
              page is the last-updated date. For material changes that
              affect how your data is processed (e.g. a new category of
              recipient, a new legal basis), we will make reasonable
              efforts to notify existing customers by email in advance
              before the change takes effect.
            </p>
          </Section>

          {/* §12 Contact */}
          <Section icon={Mail} title="12. Contact us about privacy">
            <p>Any privacy question or request:</p>
            <p className="mt-2">
              <a
                href="mailto:saturnstyling@gmail.com"
                className="text-saturn-orange hover:underline text-lg"
                data-testid="privacy-contact-email"
              >
                saturnstyling@gmail.com
              </a>
            </p>
            <p className="mt-4 text-sm text-slate-400">
              This policy sets a strong compliance floor but is not a
              substitute for tailored legal advice. If you are relying on
              this document for a specific compliance decision, please
              consult a qualified solicitor.
            </p>
          </Section>
        </motion.div>
      </main>
    </div>
  );
};

// ---- Local layout helpers -------------------------------------------------

const Section = ({ icon: Icon, title, children }) => (
  <section className="glass rounded-xl p-6 mb-6" data-testid={`privacy-section-${slug(title)}`}>
    <h2 className="font-rajdhani font-bold text-xl text-white mb-3 flex items-center gap-2">
      <Icon className="w-5 h-5 text-saturn-orange" />
      {title}
    </h2>
    <div className="text-slate-300 leading-relaxed space-y-2">{children}</div>
  </section>
);

const Li = ({ children }) => (
  <li className="flex items-start gap-2">
    <span className="text-saturn-orange mt-1.5">•</span>
    <span className="flex-1">{children}</span>
  </li>
);

const Row = ({ d, b }) => (
  <tr className="border-t border-slate-800">
    <td className="align-top px-3 py-2">{d}</td>
    <td className="align-top px-3 py-2 text-slate-400">{b}</td>
  </tr>
);

const slug = (s) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default PrivacyPolicyPage;
